import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { get, put, del, post } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";
import { toast } from "../../Utils/Helper/toast";

export interface NotificationSettings {
  id?: string;
  userId?: string;
  notificationTimeRangeHours: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface UpdateNotificationSettingsPayload {
  notification_time_range_hours: number;
}

export interface Notification {
  id: string;
  recipientId: string;
  recipientType: string;
  recipientName: string | null;
  createdBy: string;
  createdByName: string;
  createdByType: string;
  type: string;
  title: string;
  message: string;
  data: Record<string, any>;
  priority: string;
  isRead: boolean;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
  executiveId: string | null;
  executiveName: string | null;
}

export interface NotificationsResponse {
  notifications: Notification[];
  total: number;
  unread: number;
  limit: number;
  offset: number;
}

interface NotificationState {
  settings: NotificationSettings | null;
  loading: boolean;
  error: string | null;
  updateLoading: boolean;
  updateError: string | null;
  updateSuccess: boolean;
  sendEmailLoading: boolean;
  sendEmailError: string | null;
  sendEmailSuccess: boolean;
  notifications: Notification[];
  notificationsLoading: boolean;
  notificationsError: string | null;
  total: number;
  unread: number;
  lastUpdated: string | null;
  hasInitialData: boolean;
  markingRead: boolean;
  deletingNotification: boolean;
  deleteError: string | null;
}

const initialState: NotificationState = {
  settings: null,
  loading: false,
  error: null,
  updateLoading: false,
  updateError: null,
  updateSuccess: false,
  sendEmailLoading: false,
  sendEmailError: null,
  sendEmailSuccess: false,
  notifications: [],
  notificationsLoading: false,
  notificationsError: null,
  total: 0,
  unread: 0,
  lastUpdated: null,
  hasInitialData: false,
  markingRead: false,
  deletingNotification: false,
  deleteError: null,
};

export const fetchNotificationSettings = createAsyncThunk(
  "notification/fetchSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.getNotificationSettings());
      return response as NotificationSettings;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch notification settings";
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateNotificationSettings = createAsyncThunk(
  "notification/updateSettings",
  async (payload: UpdateNotificationSettingsPayload, { rejectWithValue }) => {
    try {
      const response = await put(
        API_URLS.updateNotificationSettings(),
        payload
      );
      return response as NotificationSettings;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update notification settings";
      return rejectWithValue(errorMessage);
    }
  }
);

export const sendAdminTestEmail = createAsyncThunk(
  "notification/sendAdminTestEmail",
  async (
    payload: { email_type: string; recipient_email: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await post(API_URLS.sendAdminEmail(), payload);
      return response;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send test email";
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchNotifications = createAsyncThunk(
  "notification/fetchNotifications",
  async (
    params: { limit?: number; offset?: number; search?: string } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await get(API_URLS.getNotifications(params));
      return response as NotificationsResponse;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch notifications";
      return rejectWithValue(errorMessage);
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  "notification/markRead",
  async (payload: { id?: string; markAll?: boolean }, { rejectWithValue }) => {
    try {
      if (payload.markAll) {
        await put(API_URLS.markNotificationRead(), {
          is_all_marked: true,
        });
        return { markAll: true };
      } else {
        await put(API_URLS.markNotificationRead(), {
          notification_id: payload.id,
        });
        return { id: payload.id };
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || payload.markAll
          ? "Failed to mark all notifications as read"
          : "Failed to mark notification as read";
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteNotification = createAsyncThunk(
  "notification/delete",
  async (notificationId: string, { rejectWithValue }) => {
    try {
      await del(API_URLS.deleteNotification(notificationId));
      return { id: notificationId };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete notification";
      return rejectWithValue(errorMessage);
    }
  }
);

const notificationSlice = createSlice({
  name: "notification",
  initialState,
  reducers: {
    clearUpdateSuccess: state => {
      state.updateSuccess = false;
    },
    clearError: state => {
      state.error = null;
      state.updateError = null;
      state.notificationsError = null;
      state.sendEmailError = null;
    },
    setLastUpdated: (state, action) => {
      state.lastUpdated = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchNotificationSettings.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotificationSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload;
        state.error = null;
      })
      .addCase(fetchNotificationSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateNotificationSettings.pending, state => {
        state.updateLoading = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateNotificationSettings.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.settings = action.payload;
        state.updateSuccess = true;
        state.updateError = null;
        toast.success(
          "Your notification settings have been updated successfully."
        );
      })
      .addCase(updateNotificationSettings.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload as string;
        state.updateSuccess = false;
        toast.error(
          (action.payload as string) ||
            "An error occurred while updating notification settings. Please try again."
        );
      })
      .addCase(sendAdminTestEmail.pending, state => {
        state.sendEmailLoading = true;
        state.sendEmailError = null;
        state.sendEmailSuccess = false;
      })
      .addCase(sendAdminTestEmail.fulfilled, state => {
        state.sendEmailLoading = false;
        state.sendEmailSuccess = true;
      })
      .addCase(sendAdminTestEmail.rejected, (state, action) => {
        state.sendEmailLoading = false;
        state.sendEmailError = action.payload as string;
        state.sendEmailSuccess = false;
      })
      .addCase(fetchNotifications.pending, state => {
        // Only show loading if we don't have initial data yet
        if (!state.hasInitialData) {
          state.notificationsLoading = true;
        }
        state.notificationsError = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.notificationsLoading = false;
        state.notifications = action.payload.notifications;
        state.total = action.payload.total;
        state.unread = action.payload.unread;
        state.lastUpdated = new Date().toLocaleTimeString();
        state.notificationsError = null;
        state.hasInitialData = true;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.notificationsLoading = false;
        state.notificationsError = action.payload as string;
      })
      .addCase(markNotificationRead.pending, state => {
        state.markingRead = true;
      })
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        state.markingRead = false;
        if (action.payload.markAll) {
          state.notifications.forEach(notification => {
            notification.isRead = true;
          });
          state.unread = 0;
        } else if (action.payload.id) {
          const notification = state.notifications.find(
            n => n.id === action.payload.id
          );
          if (notification) {
            notification.isRead = true;
            state.unread = Math.max(0, state.unread - 1);
          }
        }
      })
      .addCase(markNotificationRead.rejected, state => {
        state.markingRead = false;
      })
      .addCase(deleteNotification.pending, state => {
        state.deletingNotification = true;
        state.deleteError = null;
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        state.deletingNotification = false;
        // Find the notification before removing it to check if it was unread
        const deletedNotification = state.notifications.find(
          n => n.id === action.payload.id
        );
        // Remove the notification
        state.notifications = state.notifications.filter(
          n => n.id !== action.payload.id
        );
        state.total = Math.max(0, state.total - 1);
        // Decrease unread count if the deleted notification was unread
        if (deletedNotification && !deletedNotification.isRead) {
          state.unread = Math.max(0, state.unread - 1);
        }
        state.deleteError = null;
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        state.deletingNotification = false;
        state.deleteError = action.payload as string;
      });
  },
});

export const { clearUpdateSuccess, clearError, setLastUpdated } =
  notificationSlice.actions;

export default notificationSlice.reducer;
